/**
 * ============================================
 * SETTINGS MODAL
 * ============================================
 * 
 * Settings panel with:
 * - Master enable/disable toggle
 * - Volume control
 * - Custom alarm sound upload
 * - Alarm mode: every refresh OR custom cooldown
 * - Per-environment toggles (affects which tabs show in dashboard)
 * - Test notification, alarm, and simulate buttons
 */

import { useState, useEffect, useRef } from 'react';
import { 
  X, Settings, Bell, BellRing, Volume2, VolumeX, CheckCircle, Power, 
  Clock, RefreshCw, Upload, Trash2, Music, AlertTriangle 
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAlerts } from '@/context/AlertContext';
import { useEnvironments } from '@/hooks';
import { 
  requestNotificationPermission, 
  sendNotification, 
  playAlarm, 
  stopAlarm,
  isAlarmPlaying,
  getAlertSettings,
  setAlertVolume,
  setAlertsEnabled,
  setAlarmMode,
  setCooldownMinutes,
  setEnvironmentEnabled,
  saveCustomSound,
  removeCustomSound,
  hasCustomSound,
  type AlarmMode,
} from '@/utils/notifications';
import type { Alert } from '@/types';

// Audible alarms are disabled product-wide (replaced by the planned voice-alert
// escalation). Hide all alarm-only settings — custom sound, alarm mode, volume,
// and the test-alarm button. Notifications are unaffected. Flip to true if
// audible alarms are ever reinstated.
const SHOW_ALARM_SETTINGS = false;

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEnvironmentsChange?: (enabledEnvs: Record<string, boolean>) => void;
}

export function SettingsModal({ isOpen, onClose, onEnvironmentsChange }: SettingsModalProps) {
  const { state, requestPermission, syncAlarm, addAlert } = useAlerts();
  const { environments: envList } = useEnvironments();
  const [testAlarmPlaying, setTestAlarmPlaying] = useState(false);
  const [notificationSent, setNotificationSent] = useState(false);
  const [simulationActive, setSimulationActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Load settings
  const [alertsEnabled, setAlertsEnabledState] = useState(true);
  const [volume, setVolumeState] = useState(1.0);
  const [alarmMode, setAlarmModeState] = useState<AlarmMode>('every_refresh');
  const [cooldownMins, setCooldownMinsState] = useState(15);
  const [envEnabled, setEnvEnabledState] = useState<Record<string, boolean>>({});
  const [customSoundName, setCustomSoundName] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      const settings = getAlertSettings();
      setAlertsEnabledState(settings.enabled);
      setVolumeState(settings.volume);
      setAlarmModeState(settings.alarmMode);
      setCooldownMinsState(settings.cooldownMinutes);
      setEnvEnabledState(settings.enabledEnvironments);
      setCustomSoundName(settings.customSoundName);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleToggleEnabled = () => {
    const newValue = !alertsEnabled;
    setAlertsEnabledState(newValue);
    setAlertsEnabled(newValue);
    
    if (!newValue) {
      stopAlarm();
      setTestAlarmPlaying(false);
    } else {
      const hasCriticalAlerts = state.activeAlerts.some(
        a => a.severity === 'critical' && !a.acknowledged
      );
      
      if (hasCriticalAlerts) {
        syncAlarm();
        setTimeout(() => {
          playAlarm();
        }, 100);
      }
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolumeState(newVolume);
    setAlertVolume(newVolume);
  };

  const handleAlarmModeChange = (mode: AlarmMode) => {
    setAlarmModeState(mode);
    setAlarmMode(mode);
  };

  const handleCooldownChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const mins = parseInt(e.target.value, 10) || 1;
    setCooldownMinsState(mins);
    setCooldownMinutes(mins);
  };

  const handleEnvToggle = (envId: string) => {
    const newValue = !envEnabled[envId];
    const newEnvEnabled = { ...envEnabled, [envId]: newValue };
    setEnvEnabledState(newEnvEnabled);
    setEnvironmentEnabled(envId, newValue);
    
    // Notify parent about environment changes
    if (onEnvironmentsChange) {
      onEnvironmentsChange(newEnvEnabled);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const success = await saveCustomSound(file);
    if (success) {
      setCustomSoundName(file.name);
    } else {
      alert('Failed to upload sound. Make sure it\'s an audio file under 5MB.');
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveCustomSound = () => {
    removeCustomSound();
    setCustomSoundName(null);
  };

  const handleTestNotification = async () => {
    if (!alertsEnabled) {
      alert('Alerts are disabled. Enable them first.');
      return;
    }

    const permission = await requestNotificationPermission();
    
    if (permission !== 'granted') {
      alert('Please enable notifications in your browser settings to test this feature.');
      return;
    }

    const testAlert: Alert = {
      id: `test-${Date.now()}`,
      type: 'sql_cpu',
      severity: 'warning',
      environmentId: 'test',
      environmentName: 'Test Environment',
      title: '🔔 Test Notification',
      message: 'This is a test notification from Lokko Overseer. If you can see this, notifications are working!',
      timestamp: new Date(),
      acknowledged: false,
    };

    sendNotification(testAlert);
    setNotificationSent(true);
    setTimeout(() => setNotificationSent(false), 3000);
  };

  const handleTestAlarm = () => {
    if (!alertsEnabled) {
      alert('Alerts are disabled. Enable them first.');
      return;
    }

    if (testAlarmPlaying || isAlarmPlaying()) {
      stopAlarm();
      setTestAlarmPlaying(false);
    } else {
      playAlarm();
      setTestAlarmPlaying(true);
      setTimeout(() => {
        stopAlarm();
        setTestAlarmPlaying(false);
      }, 5000);
    }
  };

  const handleSimulateError = () => {
    if (!alertsEnabled) {
      alert('Alerts are disabled. Enable them first.');
      return;
    }

    const simulatedAlert: Alert = {
      id: `simulated-${Date.now()}`,
      type: 'nakama_down',
      severity: 'critical',
      environmentId: 'live',
      environmentName: 'Production (Simulated)',
      title: '🚨 SIMULATED: Nakama Down!',
      message: 'This is a SIMULATED critical alert for testing purposes. Nakama game server is not responding.',
      timestamp: new Date(),
      acknowledged: false,
    };

    addAlert(simulatedAlert);
    setSimulationActive(true);
    
    setTimeout(() => {
      setSimulationActive(false);
    }, 10000);
  };

  const permissionStatus = state.notificationPermission;
  const volumePercent = Math.round(volume * 100);
  const hasCustomSoundFile = hasCustomSound();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-background border border-border rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-background z-10">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Alert Settings</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Master Enable/Disable Toggle */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Power className="h-4 w-4" />
              Alerts & Notifications
            </h3>
            <div 
              className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors ${
                alertsEnabled 
                  ? 'bg-green-500/20 border border-green-500/30' 
                  : 'bg-red-500/20 border border-red-500/30'
              }`}
              onClick={handleToggleEnabled}
            >
              <div className="flex items-center gap-3">
                {alertsEnabled ? (
                  <BellRing className="h-5 w-5 text-green-500" />
                ) : (
                  <VolumeX className="h-5 w-5 text-red-500" />
                )}
                <div>
                  <p className="font-medium">{alertsEnabled ? 'Enabled' : 'Disabled'}</p>
                  <p className="text-xs text-muted-foreground">
                    {alertsEnabled ? 'Notifications fire for critical alerts' : 'All notifications disabled'}
                  </p>
                </div>
              </div>
              <div className={`w-12 h-7 rounded-full p-0.5 transition-colors ${
                alertsEnabled ? 'bg-green-500' : 'bg-gray-600'
              }`}>
                <div className={`w-6 h-6 rounded-full bg-white transition-transform ${
                  alertsEnabled ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </div>
            </div>
          </div>

          {/* Alarm-only settings (custom sound + alarm mode) — hidden */}
          {SHOW_ALARM_SETTINGS && (<>
          <div className={`space-y-3 ${!alertsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Music className="h-4 w-4" />
              Custom Alarm Sound
            </h3>
            <div className="space-y-2">
              {hasCustomSoundFile ? (
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Music className="h-4 w-4 text-green-500" />
                    <span className="text-sm truncate max-w-[200px]">{customSoundName || 'Custom sound'}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleRemoveCustomSound}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Using default alarm sound</p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Custom Sound
              </Button>
            </div>
          </div>

          {/* Alarm Mode */}
          <div className={`space-y-3 ${!alertsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Alarm Mode
            </h3>
            <div className="space-y-2">
              <div 
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  alarmMode === 'every_refresh' ? 'bg-primary/20 border border-primary/30' : 'bg-muted/30 hover:bg-muted/50'
                }`}
                onClick={() => handleAlarmModeChange('every_refresh')}
              >
                <RefreshCw className={`h-4 w-4 ${alarmMode === 'every_refresh' ? 'text-primary' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-sm font-medium">Every Refresh</p>
                  <p className="text-xs text-muted-foreground">Alarm plays on each data refresh while alert is active</p>
                </div>
              </div>
              <div 
                className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                  alarmMode === 'cooldown' ? 'bg-primary/20 border border-primary/30' : 'bg-muted/30 hover:bg-muted/50'
                }`}
                onClick={() => handleAlarmModeChange('cooldown')}
              >
                <Clock className={`h-4 w-4 ${alarmMode === 'cooldown' ? 'text-primary' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-sm font-medium">Cooldown</p>
                  <p className="text-xs text-muted-foreground">Alarm plays once, then waits before playing again</p>
                </div>
              </div>
              
              {alarmMode === 'cooldown' && (
                <div className="flex items-center gap-3 pl-7 pt-2">
                  <label className="text-sm text-muted-foreground">Cooldown:</label>
                  <input
                    type="number"
                    min="1"
                    max="1440"
                    value={cooldownMins}
                    onChange={handleCooldownChange}
                    className="w-20 px-3 py-1.5 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <span className="text-sm text-muted-foreground">minutes</span>
                </div>
              )}
            </div>
          </div>
          </>)}

          {/* Per-Environment Toggles */}
          <div className={`space-y-3 ${!alertsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Environments to Monitor
            </h3>
            <p className="text-xs text-muted-foreground">
              Disabled environments won't trigger notifications but will still show in dashboard tabs.
            </p>
            <div className="space-y-2">
              {envList.map((env) => (
                <div 
                  key={env.id}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      envEnabled[env.id] !== false ? 'bg-green-500' : 'bg-gray-500'
                    }`} />
                    <span className="font-medium">{env.name}</span>
                    <span className="text-xs text-muted-foreground">({env.shortName})</span>
                  </div>
                  <button
                    onClick={() => handleEnvToggle(env.id)}
                    className={`w-10 h-6 rounded-full p-0.5 transition-colors ${
                      envEnabled[env.id] !== false ? 'bg-green-500' : 'bg-gray-600'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                      envEnabled[env.id] !== false ? 'translate-x-4' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Volume Control (alarm-only) — hidden */}
          {SHOW_ALARM_SETTINGS && (
          <div className={`space-y-3 ${!alertsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Volume2 className="h-4 w-4" />
              Alarm Volume
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-4">
                <VolumeX className="h-4 w-4 text-muted-foreground" />
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <Volume2 className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>0%</span>
                <span className={`font-bold ${volumePercent >= 80 ? 'text-red-400' : volumePercent >= 50 ? 'text-yellow-400' : ''}`}>
                  {volumePercent}%
                </span>
                <span>100%</span>
              </div>
              {volumePercent >= 80 && (
                <p className="text-xs text-red-400">⚠️ Very loud! Make sure your speakers can handle it.</p>
              )}
            </div>
          </div>
          )}

          {/* Notification Permission Status */}
          <div className={`space-y-3 ${!alertsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <h3 className="text-sm font-medium flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Browser Notification Permission
            </h3>
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${
                  permissionStatus === 'granted' ? 'bg-green-500' :
                  permissionStatus === 'denied' ? 'bg-red-500' : 'bg-yellow-500'
                }`} />
                <span className="text-sm">
                  {permissionStatus === 'granted' ? 'Enabled' :
                   permissionStatus === 'denied' ? 'Blocked' : 'Not Set'}
                </span>
              </div>
              {permissionStatus !== 'granted' && (
                <Button variant="outline" size="sm" onClick={requestPermission}>
                  Enable
                </Button>
              )}
            </div>
          </div>

          {/* Test Buttons */}
          <div className={`space-y-3 pt-3 border-t border-border ${!alertsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <h3 className="text-sm font-medium">Test</h3>
            <div className={`grid ${SHOW_ALARM_SETTINGS ? 'grid-cols-3' : 'grid-cols-2'} gap-2`}>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleTestNotification}
                disabled={!alertsEnabled || permissionStatus === 'denied'}
                className="flex-col h-auto py-2"
              >
                {notificationSent ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <Bell className="h-4 w-4" />
                )}
                <span className="text-xs mt-1">Notification</span>
              </Button>
              {SHOW_ALARM_SETTINGS && (
              <Button
                variant={testAlarmPlaying ? "destructive" : "outline"}
                size="sm"
                onClick={handleTestAlarm}
                disabled={!alertsEnabled}
                className="flex-col h-auto py-2"
              >
                {testAlarmPlaying ? (
                  <VolumeX className="h-4 w-4" />
                ) : (
                  <BellRing className="h-4 w-4" />
                )}
                <span className="text-xs mt-1">{testAlarmPlaying ? 'Stop' : 'Alarm'}</span>
              </Button>
              )}
              <Button 
                variant={simulationActive ? "destructive" : "outline"}
                size="sm"
                onClick={handleSimulateError}
                disabled={!alertsEnabled || simulationActive}
                className="flex-col h-auto py-2"
              >
                <AlertTriangle className={`h-4 w-4 ${simulationActive ? 'animate-pulse' : ''}`} />
                <span className="text-xs mt-1">Simulate</span>
              </Button>
            </div>
            {SHOW_ALARM_SETTINGS && testAlarmPlaying && (
              <div className="flex items-center gap-2 text-sm text-yellow-400 animate-pulse">
                <BellRing className="h-4 w-4" />
                Alarm playing at {volumePercent}% volume... (auto-stops in 5s)
              </div>
            )}
            {simulationActive && (
              <div className="flex items-center gap-2 text-sm text-red-400 animate-pulse">
                <AlertTriangle className="h-4 w-4" />
                Simulated critical alert active! Check the alarm modal.
              </div>
            )}
          </div>

          {/* Alert Thresholds Info */}
          <div className="space-y-3 pt-3 border-t border-border">
            <h3 className="text-sm font-medium">Alert Triggers</h3>
            <div className="text-xs text-muted-foreground space-y-1.5">
              <div className="flex items-center justify-between">
                <span>🎮 Nakama Down</span>
                <span className="text-red-400">CRITICAL</span>
              </div>
              <div className="flex items-center justify-between">
                <span>🪳 CockroachDB Down</span>
                <span className="text-red-400">CRITICAL</span>
              </div>
              <div className="flex items-center justify-between">
                <span>🖥️ EC2 Instances = 0</span>
                <span className="text-red-400">CRITICAL</span>
              </div>
              <div className="flex items-center justify-between">
                <span>🔴 Redis Down</span>
                <span className="text-red-400">CRITICAL</span>
              </div>
              <div className="flex items-center justify-between">
                <span>📊 ASG Instances = 0</span>
                <span className="text-red-400">CRITICAL</span>
              </div>
            </div>
            
            {/* CCU Thresholds */}
            <div className="mt-4 pt-3 border-t border-border/50">
              <h4 className="text-xs font-medium text-orange-400 mb-2">🎉 CCU Milestone Notifications</h4>
              <p className="text-xs text-muted-foreground mb-2">
                Triggers once per threshold, stored locally per device:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {[100, 250, 500, 1500, 2500, 4000, 5000, 7500, 9000, 10000].map((threshold) => (
                  <span 
                    key={threshold}
                    className="px-2 py-0.5 text-xs bg-orange-500/20 text-orange-400 rounded"
                  >
                    {threshold.toLocaleString()}
                  </span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Clear browser cache to reset acknowledged thresholds.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
